import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Flex from 'components/Flex';
import Text from 'components/Text';
import Button from 'components/Button';
import VisuallyHidden from 'components/VisuallyHidden';
import TooltipButton from 'components/TooltipButton';
import Icon from 'components/Icon';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var RESEND_COOLDOWN_MS = 60000;
function getRemainingCooldownMs(validationSentTime) {
  if (!validationSentTime) {
    return 0;
  }
  var elapsed = Date.now() - new Date(validationSentTime).getTime();
  return Math.max(0, RESEND_COOLDOWN_MS - elapsed);
}
function EmailListItem(_ref) {
  var userEmail = _ref.userEmail,
    onRemove = _ref.onRemove,
    onResend = _ref.onResend,
    isResending = _ref.isResending;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var id = userEmail.id,
    email = userEmail.email,
    isValidated = userEmail.isValidated,
    validationSentTime = userEmail.validationSentTime;
  var _useState = useState(function () {
      return getRemainingCooldownMs(validationSentTime) > 0;
    }),
    _useState2 = _slicedToArray(_useState, 2),
    isCoolingDown = _useState2[0],
    setIsCoolingDown = _useState2[1];
  var cooldownTimeoutRef = useRef(undefined);

  // Re-derive the cooldown from the server-recorded send time whenever it
  // changes (including after a remount, e.g. navigating away and back
  // within the cooldown window) so the button stays disabled for the
  // correct remaining duration rather than resetting to a fresh 60s.
  useEffect(function () {
    clearTimeout(cooldownTimeoutRef.current);
    var remaining = getRemainingCooldownMs(validationSentTime);
    if (remaining <= 0) {
      setIsCoolingDown(false);
      return;
    }
    setIsCoolingDown(true);
    cooldownTimeoutRef.current = setTimeout(function () {
      return setIsCoolingDown(false);
    }, remaining);
    return function () {
      return clearTimeout(cooldownTimeoutRef.current);
    };
  }, [validationSentTime]);
  function handleResend() {
    return _handleResend.apply(this, arguments);
  }
  function _handleResend() {
    _handleResend = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var _t;
      return _regeneratorRuntime.wrap(function (_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            // Disable immediately for instant feedback; the effect above will
            // reconcile with the real remaining time once the resend succeeds
            // and the updated validationSentTime is refetched.
            setIsCoolingDown(true);
            _context.prev = 1;
            _context.next = 2;
            return onResend(id);
          case 2:
            _context.next = 4;
            break;
          case 3:
            _context.prev = 3;
            _t = _context["catch"](1);
            // The resend failed, so validationSentTime won't have changed -
            // re-enable unless an earlier resend is still within its cooldown.
            setIsCoolingDown(getRemainingCooldownMs(validationSentTime) > 0);
          case 4:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[1, 3]]);
    }));
    return _handleResend.apply(this, arguments);
  }
  var isDisabled = isResending || isCoolingDown;
  return /*#__PURE__*/_jsxs(Flex, {
    background: "shaded",
    grow: true,
    borderRadius: "large",
    px: "s",
    py: "xs",
    gap: "xs",
    children: [!isValidated ? /*#__PURE__*/_jsx(Icon, {
      name: "bang",
      color: "alert-fail"
    }) : /*#__PURE__*/_jsx(Icon, {
      name: "ok",
      color: "alert-success"
    }), /*#__PURE__*/_jsx(Flex, {
      grow: true,
      shrink: true,
      gap: "s",
      children: /*#__PURE__*/_jsx(Text, {
        bold: true,
        overflow: "wrap",
        children: email
      })
    }), !isValidated && /*#__PURE__*/_jsxs(TooltipButton, {
      size: "small",
      variant: "important",
      isDisabled: isDisabled,
      onClick: handleResend,
      icon: "send",
      title: t(isDisabled ? 'hint_resend_verification_email_disabled' : 'hint_resend_verification_email'),
      children: [t(isDisabled ? 'state_pending_verification' : 'actions_resend_verification'), /*#__PURE__*/_jsxs(VisuallyHidden, {
        children: [" ", email]
      })]
    }), /*#__PURE__*/_jsx(Button, {
      icon: "trash",
      iconOnly: true,
      variant: "transparent",
      size: "small",
      onClick: function onClick() {
        return onRemove(id);
      },
      children: /*#__PURE__*/_jsx(VisuallyHidden, {
        children: t('actions_remove_name', {
          name: email
        })
      })
    })]
  });
}
export default EmailListItem;